/**
 * InactivityModal Component
 *
 * Displays a warning modal when the user's session is about to expire
 * due to inactivity. Shows a countdown and options to stay logged in or logout.
 *
 * Part of: AI Platform v5.5 - Session Management
 */

import React, { useState, useEffect, useCallback } from 'react';
import './InactivityModal.css';

interface InactivityModalProps {
  /** Whether the modal is visible */
  isOpen: boolean;
  /** Countdown duration in seconds (default: 30) */
  countdown?: number;
  /** Called when user chooses to stay logged in */
  onStayLoggedIn: () => void;
  /** Called when user chooses to logout or countdown expires */
  onLogout: () => void;
}

export const InactivityModal: React.FC<InactivityModalProps> = ({
  isOpen,
  countdown: initialCountdown = 30,
  onStayLoggedIn,
  onLogout
}) => {
  const [remaining, setRemaining] = useState(initialCountdown);

  // Reset countdown when modal opens
  useEffect(() => {
    if (isOpen) {
      setRemaining(initialCountdown);
    }
  }, [isOpen, initialCountdown]);

  // Countdown timer
  useEffect(() => {
    if (!isOpen) return;

    const interval = setInterval(() => {
      setRemaining(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          // Use setTimeout to avoid calling during render
          setTimeout(() => onLogout(), 0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, onLogout]);

  // Handle keyboard events
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!isOpen) return;

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onStayLoggedIn();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onLogout();
    }
  }, [isOpen, onStayLoggedIn, onLogout]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!isOpen) return null;

  // Calculate progress percentage for visual indicator
  const progress = (remaining / initialCountdown) * 100;

  return (
    <div className="inactivity-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="inactivity-title">
      <div className="inactivity-modal">
        {/* Progress bar */}
        <div className="inactivity-progress-bar">
          <div
            className="inactivity-progress-fill"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Icon */}
        <div className="inactivity-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        </div>

        {/* Title */}
        <h2 id="inactivity-title" className="inactivity-title">
          Session Timeout
        </h2>

        {/* Message */}
        <p className="inactivity-message">
          Your session is about to expire due to inactivity.
        </p>

        {/* Countdown */}
        <div className="inactivity-countdown">
          <span className="countdown-number">{remaining}</span>
          <span className="countdown-label">seconds remaining</span>
        </div>

        {/* Actions */}
        <div className="inactivity-actions">
          <button
            className="btn-logout"
            onClick={onLogout}
            type="button"
          >
            Logout Now
          </button>
          <button
            className="btn-stay"
            onClick={onStayLoggedIn}
            autoFocus
            type="button"
          >
            Stay Logged In
          </button>
        </div>

        {/* Keyboard hint */}
        <p className="inactivity-hint">
          Press <kbd>Enter</kbd> to stay or <kbd>Esc</kbd> to logout
        </p>
      </div>
    </div>
  );
};

export default InactivityModal;
